import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Shield, 
  Users, 
  Globe,
  Award,
  CheckCircle,
  Star,
  Target,
  Calendar,
  Building,
  Heart
} from 'lucide-react';

// Import professional images
const aboutHeroImage = require('../../assets/business-team-singapore.jpg');
const teamImage = require('../../assets/team-investors-analyzing-profit-amount-making-decisions_482257-88610.jpg_jptdn5.jpg');
const officeImage = require('../../assets/day-office-travel-agency_23-2150769936.jpg_ijmk2n.jpg');

const About = () => {
  const achievements = [
    { icon: Users, number: '1M+', label: 'Active Traders Worldwide' },
    { icon: Globe, number: '150+', label: 'Countries Served' },
    { icon: TrendingUp, number: '$50B+', label: 'Annual Trading Volume' },
    { icon: Award, number: '15+', label: 'Years of Excellence' }
  ];

  const values = [
    {
      icon: <Shield className="h-12 w-12 text-blue-600" />,
      title: 'Security First',
      description: 'We prioritize the security of your investments and personal data with bank-grade protection and insurance coverage.',
      color: 'blue'
    },
    {
      icon: <Target className="h-12 w-12 text-emerald-600" />,
      title: 'Innovation',
      description: 'Cutting-edge technology and continuous platform improvements to provide you with the best trading experience.',
      color: 'emerald'
    },
    {
      icon: <Heart className="h-12 w-12 text-red-600" />,
      title: 'Customer Focus',
      description: 'Your success is our priority. We provide 24/7 support and educational resources to help you achieve your goals.',
      color: 'red'
    },
    {
      icon: <Star className="h-12 w-12 text-yellow-600" />,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from platform performance to customer service quality.',
      color: 'yellow'
    }
  ];

  const milestones = [
    { year: '2010', title: 'Company Founded', description: 'ChartLine Brokerage was established with a vision to democratize trading.' },
    { year: '2013', title: 'Mobile Platform Launch', description: 'Launched our first mobile trading app for iOS and Android.' },
    { year: '2016', title: 'Crypto Integration', description: 'Added cryptocurrency trading to our platform portfolio.' },
    { year: '2019', title: 'Global Expansion', description: 'Expanded services to over 100 countries worldwide.' },
    { year: '2021', title: 'Zero Commission', description: 'Introduced zero-commission stock trading for all users.' },
    { year: '2024', title: 'AI-Powered Tools', description: 'Integrated AI-powered analytics and trading insights.' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="about-hero-content"
            >
              <div className="about-hero-badge">
                <Building className="about-hero-badge-icon" />
                <span>About ChartLine Brokerage</span>
              </div>
              <h1 className="about-hero-title">
                Empowering Traders
                <span className="about-hero-title-gradient"> Worldwide</span>
              </h1>
              <p className="about-hero-subtitle">
                For over 15 years, ChartLine Brokerage has been at the forefront of financial innovation, 
                providing cutting-edge trading technology and exceptional service to millions of traders globally.
              </p>
              <div className="about-hero-stats">
                <div className="about-hero-stat">
                  <div className="about-hero-stat-number">15+</div>
                  <div className="about-hero-stat-label">Years</div>
                </div>
                <div className="about-hero-stat">
                  <div className="about-hero-stat-number">1M+</div>
                  <div className="about-hero-stat-label">Traders</div>
                </div>
                <div className="about-hero-stat">
                  <div className="about-hero-stat-number">150+</div>
                  <div className="about-hero-stat-label">Countries</div>
                </div>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="about-hero-image"
            >
              <div className="relative">
                <img
                  src={aboutHeroImage}
                  alt="ChartLine Brokerage Team"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-2xl" />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-slate-800">Trusted by Millions</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="about-mission-section">
        <div className="about-mission-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="about-mission-card"
            >
              <div className="about-mission-icon">
                <Target className="h-16 w-16 text-blue-600" />
              </div>
              <h2 className="about-mission-title">Our Mission</h2>
              <p className="about-mission-text">
                To democratize access to global financial markets by providing innovative, secure, and 
                user-friendly trading platforms that empower individuals to achieve their financial goals.
              </p>
              <ul className="about-mission-list">
                <li>Make trading accessible to everyone</li>
                <li>Provide cutting-edge technology</li>
                <li>Ensure transparent pricing</li>
                <li>Deliver exceptional customer service</li>
              </ul>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="about-mission-card"
            >
              <div className="about-mission-icon">
                <Globe className="h-16 w-16 text-emerald-600" />
              </div>
              <h2 className="about-mission-title">Our Vision</h2>
              <p className="about-mission-text">
                To become the world's most trusted and innovative brokerage platform, setting new standards 
                for technology, security, and customer experience in the financial services industry.
              </p>
              <ul className="about-mission-list">
                <li>Lead through innovation</li>
                <li>Build lasting trust</li>
                <li>Expand global reach</li>
                <li>Shape the future of trading</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values-section">
        <div className="about-values-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="about-values-header"
          >
            <h2 className="about-values-title">Our Core Values</h2>
            <p className="about-values-subtitle">
              The principles that guide everything we do and every decision we make
            </p>
          </motion.div>

          <div className="about-values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="about-value-card"
              >
                <div className="about-value-icon">
                  {value.icon}
                </div>
                <h3 className="about-value-title">{value.title}</h3>
                <p className="about-value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="about-timeline-section">
        <div className="about-timeline-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="about-timeline-header"
          >
            <h2 className="about-timeline-title">Our Journey</h2>
            <p className="about-timeline-subtitle">
              Major milestones in our mission to revolutionize trading
            </p>
          </motion.div>

          <div className="about-timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="about-timeline-item"
              >
                <div className="about-timeline-year">{milestone.year}</div>
                <div className="about-timeline-content">
                  <h3 className="about-timeline-item-title">{milestone.title}</h3>
                  <p className="about-timeline-item-description">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="about-achievements-section">
        <div className="about-achievements-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="about-achievements-header"
          >
            <h2 className="about-achievements-title">Our Impact</h2>
            <p className="about-achievements-subtitle">
              Numbers that reflect our commitment to excellence and growth
            </p>
          </motion.div>

          <div className="about-achievements-grid">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="about-achievement-card"
              >
                <div className="about-achievement-icon">
                  <achievement.icon className="h-12 w-12 text-blue-600" />
                </div>
                <div className="about-achievement-content">
                  <div className="about-achievement-number">{achievement.number}</div>
                  <div className="about-achievement-label">{achievement.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="about-team-section">
        <div className="about-team-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="about-team-image"
            >
              <img
                src={teamImage}
                alt="ChartLine Team"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="about-team-content"
            >
              <h2 className="about-team-title">
                Built by <span className="text-blue-600">Experts</span>
              </h2>
              <p className="about-team-subtitle">
                Our team consists of financial experts, technology leaders, and customer service 
                professionals dedicated to your trading success.
              </p>
              
              <div className="about-team-stats">
                <div className="about-team-stat">
                  <div className="about-team-stat-number">500+</div>
                  <div className="about-team-stat-label">Team Members</div>
                </div>
                <div className="about-team-stat">
                  <div className="about-team-stat-number">50+</div>
                  <div className="about-team-stat-label">Countries</div>
                </div>
                <div className="about-team-stat">
                  <div className="about-team-stat-number">24/7</div>
                  <div className="about-team-stat-label">Support</div>
                </div>
              </div>

              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="about-team-cta"
              >
                Join Our Community
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="about-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="about-cta-content"
          >
            <h2 className="about-cta-title">
              Ready to Start Your Trading Journey?
            </h2>
            <p className="about-cta-subtitle">
              Join millions of traders who trust ChartLine Brokerage for their investment success. 
              Experience the difference with our award-winning platform and dedicated support.
            </p>
            <motion.a
              href="https://dashboard.chartlinebrokerage.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="about-cta-button"
            >
              Open Trading Account
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 